import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * État « lancement en cours » d'un bouton qui ouvre un exercice.
 *
 * Entre le clic et l'affichage de l'exercice il peut s'écouler plus d'une
 * seconde (résolution du chapitre, navigation) : sans retour visuel, l'élève
 * croit que le bouton ne marche pas et tape à nouveau.
 *
 * `launch(id, action)` marque `id` comme en cours puis exécute `action` :
 * - `action` renvoie `false` ou lève → l'état est levé (le bouton redevient cliquable) ;
 * - sinon l'état est CONSERVÉ : la navigation va démonter la page, le spinner
 *   reste donc visible jusqu'à l'ouverture de l'exercice.
 * Un second clic pendant un lancement est ignoré.
 */
export function useLaunchPending() {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const launch = useCallback(
    async (id: string, action: () => boolean | void | Promise<boolean | void>) => {
      if (pendingRef.current) return;
      pendingRef.current = id;
      setPendingId(id);
      const reset = () => {
        pendingRef.current = null;
        if (mountedRef.current) setPendingId(null);
      };
      try {
        const ok = await action();
        if (ok === false) reset();
      } catch (err) {
        reset();
        throw err;
      }
    },
    [],
  );

  return { pendingId, launch };
}
